import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
    ChevronLeftIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    ArrowPathIcon,
    CloudArrowUpIcon,
    TrashIcon,
    SparklesIcon,
    DocumentDuplicateIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import RichTextEditor from '../../components/teacher/RichTextEditor';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import LessonPlanModal from '../../components/teacher/LessonPlanModal';
import ConfirmModal from '../../components/common/ConfirmModal';
import { generateLessonPlanPDF } from '../../utils/pdfGenerators';

// Local Icons to ensure compatibility and stability
const ShieldCheckIcon = (props) => (
    <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
);
const QuestionMarkCircleIcon = (props) => (
    <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);
const ListBulletIcon = (props) => (
    <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
    </svg>
);
const AcademicCapIcon = (props) => (
    <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
    </svg>
);
const UserGroupIcon = (props) => (
    <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
);
const ChatBubbleBottomCenterTextIcon = (props) => (
    <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
);

const LessonPlanEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { get, put } = useApi();
    const { showToast, is_head_of_studies } = useAppState();
    const isHOS = is_head_of_studies;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [plan, setPlan] = useState(null);
    const [siblingPlans, setSiblingPlans] = useState([]);
    const [isNewPlanModalOpen, setIsNewPlanModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        lesson_date: new Date().toLocaleDateString('en-CA'),
        duration_minutes: 40,
        sub_strand_id: null,
        sub_strand_name: '',
        introduction: '',
        development: '',
        conclusion: '',
        core_competencies: [],
        values: [],
        pcis: [],
        key_inquiry_questions: '',
        learning_activities: '',
        resources_needed: '',
        assessment_strategies: '',
        differentiation: '',
        inclusivity: '',
        reflection: '',
        custom_outcomes: [],
        is_published: false,
        is_recommendation: false
    });
    const [initialData, setInitialData] = useState(null);

    const [activeSection, setActiveSection] = useState('identification');
    const [outcomesOpen, setOutcomesOpen] = useState(true);
    const [saveStatus, setSaveStatus] = useState(null); // 'success', 'error', null
    const [, setIsTemplateSaved] = useState(false);
    const [dateChangeModal, setDateChangeModal] = useState({ isOpen: false, newDate: null });
    const [showClearConfirm, setShowClearConfirm] = useState(false);
    const [switchPlanTarget, setSwitchPlanTarget] = useState(null); // { id, date } of an existing plan for the chosen date

    const coreCompetenciesOptions = [
        "Communication and Collaboration",
        "Critical Thinking and Problem Solving",
        "Creativity and Imagination",
        "Citizenship",
        "Digital Literacy",
        "Learning to Learn",
        "Self-Efficacy"
    ];

    const valuesOptions = [
        "Responsibility",
        "Respect",
        "Integrity",
        "Unity",
        "Peace",
        "Patriotism"
    ];

    const pciOptions = [
        "Environmental Conservation",
        "Health and Safety",
        "Financial Literacy",
        "Digital Citizenship",
        "Gender and Inclusion",
        "Peace Education",
        "Life Skills"
    ];

    const isItemSelected = (list, opt) => {
        if (!Array.isArray(list)) return false;
        const optLower = typeof opt === 'string' ? opt.toLowerCase() : opt;
        return list.some(i => {
            if (i === opt) return true;
            if (typeof i === 'string' && typeof optLower === 'string') {
                const iLower = i.toLowerCase();
                return iLower === optLower || iLower.startsWith(optLower + ':');
            }
            return false;
        });
    };

    // Back button: return to where the editor was opened from. Lesson Records pass an explicit target;
    // other entry points (dashboard, scheme of work) fall back to the previous page.
    const handleBack = () => {
        const returnTo = location.state?.returnTo;
        if (returnTo) {
            navigate(returnTo);
        } else if (window.history.length <= 1 && plan?.learning_area_id) {
            // Opened directly (refresh or pasted link): go to the course's lesson plans
            navigate(`/teacher/courses/${plan.learning_area_id}?tab=records&sub=lesson-plans`);
        } else {
            navigate(-1);
        }
    };

    // External modal is used now

    const fetchLessonPlan = useCallback(async () => {
        try {
            setLoading(true);
            const data = await get(`/api/cbc/lesson-plans/${id}/`);
            setPlan(data);
            const initial = {
                id: data.id,
                title: data.title || '',
                introduction: data.introduction || '',
                development: data.development || '',
                conclusion: data.conclusion || '',
                learning_activities: data.learning_activities || '',
                resources_needed: data.resources_needed || '',
                assessment_strategies: data.assessment_strategies || '',
                inclusivity: data.inclusivity || '',
                reflection: data.reflection || '',
                sub_strand: data.sub_strand?.id || data.sub_strand,
                sub_strand_id: data.sub_strand_id || data.sub_strand?.id,
                sub_strand_name: data.sub_strand_name || data.sub_strand?.name || '',
                strand_name: data.strand_name || data.sub_strand?.strand?.name || '',
                key_inquiry_questions: data.key_inquiry_questions || '',
                core_competencies: data.core_competencies || [],
                values: data.values || [],
                pcis: data.pcis || [],
                is_published: data.is_published || false,
                is_recommendation: data.is_recommendation || false,
                lesson_date: data.lesson_date || new Date().toLocaleDateString('en-CA'),
                duration_minutes: data.duration_minutes || 40,
                custom_outcomes: data.custom_outcomes || []
            };
            setFormData(initial);
            setInitialData(JSON.stringify(initial));
            setIsTemplateSaved(data.is_template || false);

            // Fetch siblings for the plan selector dropdown - Fix: use sub_strand.id
            const subStrandId = data.sub_strand?.id || data.sub_strand;
            if (subStrandId && typeof subStrandId === 'number') {
                const siblings = await get(`/api/cbc/lesson-plans/?sub_strand=${subStrandId}`);
                setSiblingPlans(siblings || []);
            }
        } catch (error) {
            console.error('Error fetching lesson plan:', error);
        } finally {
            setLoading(false);
        }
    }, [get, id]);

    useEffect(() => {
        if (id) {
            fetchLessonPlan();
        }
    }, [id, fetchLessonPlan]);
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                const btn = document.getElementById('save-progress-btn');
                if (btn && !btn.disabled) {
                    btn.click();
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleExportPDF = async () => {
        // The form data has no class details; they come from the loaded plan
        await generateLessonPlanPDF({
            ...formData,
            learning_area_name: plan?.learning_area_name,
            grade_level_name: plan?.grade_level_name,
            stream_name: plan?.stream_name,
            week_number: plan?.week_number
        });
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setSaveStatus(null);

            const payload = {
                ...formData,
                sub_strand: formData.sub_strand?.id || formData.sub_strand,
                is_template: true,
                is_recommendation: false // Mark as manual/verified once saved
            };

            await put(`/api/cbc/lesson-plans/${id}/`, payload);
            setSaveStatus('success');

            // The Lesson Plans and Records of Work lists are cached; refresh them so a new reflection
            // (Pending -> Published) shows as soon as the teacher goes back
            queryClient.invalidateQueries({ queryKey: ['lesson-plans'] });
            queryClient.invalidateQueries({ queryKey: ['records-of-work'] });

            // Update local state to reflect what we just saved
            setIsTemplateSaved(true);

            // Sync initialData with CURRENT formData to clear isDirty
            setInitialData(JSON.stringify(formData));

            setTimeout(() => setSaveStatus(null), 3000);
            showToast('Lesson plan saved successfully!');
        } catch (error) {
            console.error('Error saving lesson plan:', error);
            setSaveStatus('error');
            showToast(error.message || 'Failed to save lesson plan', 'error');
        } finally {
            setSaving(false);
        }
    };

    const isDirty = useMemo(() => {
        if (!initialData) return false;
        return initialData !== JSON.stringify(formData);
    }, [formData, initialData]);

    // Asks for confirmation (see the ConfirmModal below); the clearing itself runs on confirm
    const handleClearRecommendations = () => {
        setShowClearConfirm(true);
    };

    const confirmClearRecommendations = () => {
        setFormData({
            ...formData,
            introduction: '',
            development: '',
            conclusion: '',
            core_competencies: [],
            values: [],
            pcis: [],
            key_inquiry_questions: '',
            learning_activities: '',
            resources_needed: '',
            assessment_strategies: '',
            differentiation: '',
            is_recommendation: false
        });
        setShowClearConfirm(false);
    };

    const toggleItem = (list, item, field) => {
        const itemLower = typeof item === 'string' ? item.toLowerCase() : item;
        const existingItemIndex = list.findIndex(i => {
            if (i === item) return true;
            if (typeof i === 'string' && typeof itemLower === 'string') {
                const iLower = i.toLowerCase();
                return iLower === itemLower || iLower.startsWith(itemLower + ':');
            }
            return false;
        });

        if (existingItemIndex !== -1) {
            const newList = [...list];
            newList.splice(existingItemIndex, 1);
            setFormData({ ...formData, [field]: newList });
        } else {
            setFormData({ ...formData, [field]: [...list, item] });
        }
    };

    if (loading) return <LoadingSpinner fullScreen />;

    return (
        <div className="h-[calc(100vh-64px)] bg-gray-50 flex flex-col overflow-hidden">
            {isNewPlanModalOpen && (
                <LessonPlanModal
                    courseId={plan?.learning_area_id}
                    subStrand={{ id: formData.sub_strand_id || formData.sub_strand, name: formData.sub_strand_name }}
                    onClose={() => setIsNewPlanModalOpen(false)}
                />
            )}
            {dateChangeModal.isOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full p-6 animate-in zoom-in-95 duration-200">
                        <div className="flex items-center gap-3 mb-4 text-blue-600">
                            <ExclamationTriangleIcon className="w-6 h-6" />
                            <h3 className="text-lg font-black text-slate-900 tracking-tight">Change Target Date</h3>
                        </div>
                        <p className="text-sm font-medium text-slate-600 mb-6">
                            You are about to change the target date for this lesson plan to <strong>{dateChangeModal.newDate}</strong>. This updates the current plan's date.
                        </p>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setDateChangeModal({ isOpen: false, newDate: null })}
                                className="flex-1 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    setFormData({ ...formData, lesson_date: dateChangeModal.newDate });
                                    setDateChangeModal({ isOpen: false, newDate: null });
                                }}
                                className="flex-1 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-lg shadow-blue-200 transition-colors"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <ConfirmModal
                isOpen={showClearConfirm}
                onClose={() => setShowClearConfirm(false)}
                onConfirm={confirmClearRecommendations}
                title="Clear Recommendations"
                message="Clear all auto-generated recommendations and start fresh? Your reflection and the plan's date and title are kept."
                confirmText="Clear"
            />
            <ConfirmModal
                isOpen={Boolean(switchPlanTarget)}
                onClose={() => setSwitchPlanTarget(null)}
                onConfirm={() => {
                    const target = switchPlanTarget;
                    setSwitchPlanTarget(null);
                    navigate(`/teacher/lesson-plan/editor/${target.id}`, { replace: true, state: location.state });
                }}
                title="Plan Already Exists"
                message={`A lesson plan already exists for ${switchPlanTarget?.date}. Switch to it?`}
                confirmText="Switch to Plan"
                type="primary"
            />
            {/* Top Bar */}
            <div className="bg-white border-b px-4 py-3 flex items-center justify-between z-10 shadow-sm flex-shrink-0">
                <div className="flex items-center gap-4">
                    <button
                        onClick={handleBack}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <ChevronLeftIcon className="w-5 h-5 text-gray-600" />
                    </button>
                    <div className="flex flex-col">
                        <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest mb-1">
                            Lesson Plan Editor
                        </span>
                        <h1 className="text-2xl font-black text-[#18216D] transition-all truncate max-w-xl group-hover:text-blue-700">
                            {formData.title || 'Untitled Lesson Plan'}
                        </h1>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {!isHOS && (
                        <button
                            onClick={() => setIsNewPlanModalOpen(true)}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-blue-700 bg-blue-50 border border-blue-100 hover:bg-blue-100 rounded-lg transition-all shadow-sm active:scale-95"
                        >
                            <DocumentDuplicateIcon className="w-5 h-5" />
                            + New Plan
                        </button>
                    )}

                    <button
                        onClick={handleExportPDF}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-[#18216D] bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-all shadow-sm active:scale-95"
                        title="Export to PDF"
                    >
                        <ArrowDownTrayIcon className="w-5 h-5" />
                        Export PDF
                    </button>

                    <div className="h-6 w-px bg-gray-200 mx-1"></div>

                    {!isHOS && formData.is_recommendation && (
                        <button
                            onClick={handleClearRecommendations}
                            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors"
                        >
                            <TrashIcon className="w-4 h-4" />
                            Clear Draft
                        </button>
                    )}

                    {/* Save as Template Removed */}

                    {!isHOS && (
                        <button
                            id="save-progress-btn"
                            onClick={handleSave}
                            disabled={saving || (!isDirty && saveStatus !== 'success')}
                            className={`flex items-center gap-2 px-6 py-2 text-sm font-bold text-white rounded-lg transition-all shadow-md ${saveStatus === 'success' ? 'bg-green-600' :
                                !isDirty ? 'bg-gray-400 cursor-not-allowed opacity-80' :
                                    'bg-blue-600 hover:bg-blue-700 active:scale-95'
                                }`}
                        >
                            {saving ? (
                                <ArrowPathIcon className="w-4 h-4 animate-spin" />
                            ) : saveStatus === 'success' ? (
                                <CheckCircleIcon className="w-4 h-4" />
                            ) : (
                                <CloudArrowUpIcon className="w-4 h-4" />
                            )}
                            {saveStatus === 'success' ? 'Saved!' : 'Save Progress'}
                        </button>
                    )}
                </div>
            </div>

            <div className="flex-1 flex overflow-hidden">
                {/* Fixed Navigation Sidebar */}
                <div className="w-72 bg-white border-r overflow-y-auto hidden lg:block">
                    <div className="p-4">
                        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">
                            CBE Planning Checklist
                        </h3>
                        <nav className="space-y-1">
                            {[
                                { id: 'identification', label: '1. Identification Details', icon: CheckCircleIcon },
                                { id: 'outcomes', label: '2. Learning Outcomes', icon: SparklesIcon },
                                { id: 'pillars', label: '3-5. Pillars (Values/PCIs)', icon: ShieldCheckIcon },
                                { id: 'preparation', label: '6 & 7. Inquiry & Resources', icon: QuestionMarkCircleIcon },
                                { id: 'structure', label: '8. Lesson Development', icon: ListBulletIcon },
                                { id: 'assessment', label: '9. Assessment Methods', icon: AcademicCapIcon },
                                { id: 'inclusivity', label: '10. Differentiation', icon: UserGroupIcon },
                                { id: 'reflection', label: '11. Reflection', icon: ChatBubbleBottomCenterTextIcon }
                            ].map((item) => (
                                <button
                                    key={item.id}
                                    onClick={() => {
                                        setActiveSection(item.id);
                                        document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
                                    }}
                                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all ${activeSection === item.id
                                        ? 'bg-blue-50 text-blue-700 shadow-sm border border-blue-100'
                                        : 'text-gray-600 hover:bg-gray-50'
                                        }`}
                                >
                                    <item.icon className={`w-5 h-5 ${activeSection === item.id ? 'text-blue-600' : 'text-gray-400'}`} />
                                    {item.label}
                                </button>
                            ))}
                        </nav>

                    </div>
                </div>

                {/* Main Content Area */}
                <div className="flex-1 overflow-y-auto bg-gray-50 p-4 lg:p-8">
                    <div className="max-w-4xl mx-auto space-y-8 pb-32">

                        {/* 1. Identification Details */}
                        <section id="identification" className="bg-white rounded-3xl shadow-sm border border-gray-200 p-8">
                            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                                <div className="p-2 bg-blue-100 rounded-lg text-blue-600">1</div>
                                Identification Details
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                                        Lesson Title
                                        <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest bg-blue-50 px-2 py-0.5 rounded-md">
                                            Plan Switcher
                                        </span>
                                    </label>
                                    <div className="relative group/title-switcher">
                                        <select
                                            value={id}
                                            onChange={(e) => navigate(`/teacher/lesson-plan/editor/${e.target.value}`, { replace: true, state: location.state })}
                                            className="w-full appearance-none px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all font-bold text-[#18216D] cursor-pointer hover:border-blue-300 pr-10"
                                        >
                                            {siblingPlans.map(p => (
                                                <option key={p.id} value={p.id}>
                                                    {p.title}
                                                </option>
                                            ))}
                                            {/* Fallback if siblingPlans hasn't loaded or current isn't in it */}
                                            {!siblingPlans.find(p => p.id === parseInt(id)) && (
                                                <option value={id}>{formData.title}</option>
                                            )}
                                        </select>
                                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-blue-600 transition-transform group-hover/title-switcher:scale-110">
                                            <ChevronDownIcon className="w-5 h-5" />
                                        </div>
                                    </div>
                                    <p className="mt-2 text-xs text-gray-400 font-medium">Click to switch between plans for this sub-strand</p>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2">Date</label>
                                        <input
                                            type="date"
                                            value={formData.lesson_date}
                                            disabled={isHOS}
                                            onChange={(e) => {
                                                const newDate = e.target.value;
                                                const existingForDate = siblingPlans.find(p => p.lesson_date === newDate);

                                                if (existingForDate && existingForDate.id !== parseInt(id)) {
                                                    setSwitchPlanTarget({ id: existingForDate.id, date: newDate });
                                                } else if (newDate !== formData.lesson_date) {
                                                    setDateChangeModal({ isOpen: true, newDate });
                                                }
                                            }}
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all font-medium disabled:opacity-70"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2">Duration (Min)</label>
                                        <input
                                            type="number"
                                            value={formData.duration_minutes}
                                            disabled={isHOS}
                                            onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all font-medium disabled:opacity-70"
                                        />
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* 2. Specific Learning Outcomes */}
                        <section id="outcomes" className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden">
                            <button
                                onClick={() => setOutcomesOpen(!outcomesOpen)}
                                className="w-full flex items-center justify-between p-8 hover:bg-gray-50 transition-colors"
                            >
                                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                                    <div className="p-2 bg-purple-100 rounded-lg text-purple-600">2</div>
                                    Learning Outcomes
                                    <span className="ml-2 px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded-full border border-purple-100">
                                        {formData.custom_outcomes.length} Assigned
                                    </span>
                                </h2>
                                {outcomesOpen ? (
                                    <ChevronUpIcon className="w-6 h-6 text-gray-400" />
                                ) : (
                                    <ChevronDownIcon className="w-6 h-6 text-gray-400" />
                                )}
                            </button>

                            {outcomesOpen && (
                                <div className="px-8 pb-8">
                                    <ul className="space-y-3">
                                        {formData.custom_outcomes?.map((outcomeText, index) => {
                                            return (
                                                <li
                                                    key={index}
                                                    className="flex items-start gap-4 p-4 rounded-2xl border bg-white border-purple-200 shadow-sm transition-all"
                                                >
                                                    <CheckCircleIcon className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                                                    <span className="text-sm font-medium text-gray-900">
                                                        {outcomeText}
                                                    </span>
                                                </li>
                                            );
                                        })}
                                        {(!formData.custom_outcomes || formData.custom_outcomes.length === 0) && (
                                            <li className="p-6 text-center bg-gray-50 border border-gray-100 rounded-2xl border-dashed">
                                                <p className="text-sm text-gray-500 italic">No specific outcomes were defined for this lesson in the SOW.</p>
                                            </li>
                                        )}
                                    </ul>
                                    <div className="mt-4 bg-purple-50 rounded-2xl p-4 border border-purple-100">
                                        <p className="text-xs text-purple-700 font-medium">
                                            These outcomes are parsed directly from your Scheme of Work row. To add or modify them, update your SOW.
                                        </p>
                                    </div>
                                    {formData.custom_outcomes.length === 0 && (
                                        <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-100 flex items-center gap-3">
                                            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0" />
                                            <p className="text-sm text-amber-800 font-medium">
                                                Warning: A lesson should typically have at least one learning outcome. Consider adding one to your Scheme of Work.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </section>

                        {/* 3-5. Core Pillars */}
                        <section id="pillars" className="bg-white rounded-3xl shadow-sm border border-gray-200 p-8">
                            <h2 className="text-xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                                <div className="p-2 bg-emerald-100 rounded-lg text-emerald-600">3-5</div>
                                Core Pillars (Competencies, Values, PCIs)
                            </h2>

                            <div className="space-y-8">
                                <div>
                                    <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                                        Core Competencies <span className="text-xs font-normal text-gray-400 font-normal tracking-normal">(Select 1-3)</span>
                                    </h3>
                                    <div className="flex flex-wrap gap-2">
                                        {coreCompetenciesOptions.map(opt => (
                                            <button
                                                key={opt}
                                                disabled={isHOS}
                                                onClick={() => toggleItem(formData.core_competencies, opt, 'core_competencies')}
                                                className={`px-4 py-2 text-xs font-bold rounded-full border transition-all ${isItemSelected(formData.core_competencies, opt)
                                                    ? 'bg-blue-600 text-white border-blue-600 shadow-md scale-105'
                                                    : 'bg-white text-gray-600 border-gray-200 hover:border-blue-400'
                                                    } ${isHOS ? 'cursor-not-allowed opacity-80' : ''}`}
                                            >
                                                {opt}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div>
                                        <h3 className="text-sm font-bold text-gray-700 mb-4">Values</h3>
                                        <div className="grid grid-cols-2 gap-2">
                                            {valuesOptions.map(opt => (
                                                <button
                                                    key={opt}
                                                    disabled={isHOS}
                                                    onClick={() => toggleItem(formData.values, opt, 'values')}
                                                    className={`px-3 py-2 text-xs font-bold rounded-xl border text-left transition-all ${isItemSelected(formData.values, opt)
                                                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-md'
                                                        : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-400'
                                                        } ${isHOS ? 'cursor-not-allowed opacity-80' : ''}`}
                                                >
                                                    {opt}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-bold text-gray-700 mb-4">PCIs</h3>
                                        <div className="grid grid-cols-2 gap-2">
                                            {pciOptions.map(opt => (
                                                <button
                                                    key={opt}
                                                    disabled={isHOS}
                                                    onClick={() => toggleItem(formData.pcis, opt, 'pcis')}
                                                    className={`px-3 py-2 text-xs font-bold rounded-xl border text-left transition-all ${isItemSelected(formData.pcis, opt)
                                                        ? 'bg-amber-600 text-white border-amber-600 shadow-md'
                                                        : 'bg-white text-gray-600 border-gray-200 hover:border-amber-400'
                                                        } ${isHOS ? 'cursor-not-allowed opacity-80' : ''}`}
                                                >
                                                    {opt}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* 6 & 7. Preparation (Inquiry & Resources) */}
                        <section id="preparation" className="bg-white rounded-3xl shadow-sm border border-gray-200 p-8">
                            <h2 className="text-xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                                <div className="p-2 bg-amber-100 rounded-lg text-amber-600">6 & 7</div>
                                Preparation (Inquiry & Resources)
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <label className="block text-sm font-bold text-gray-800 mb-3 ml-1">6. Key Inquiry Question(s)</label>
                                    <RichTextEditor
                                        compact={true}
                                        readOnly={isHOS}
                                        value={formData.key_inquiry_questions}
                                        onChange={(val) => setFormData({ ...formData, key_inquiry_questions: val })}
                                        placeholder="What open-ended questions will drive the lesson?"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-800 mb-3 ml-1">7. Learning Resources</label>
                                    <RichTextEditor
                                        compact={true}
                                        readOnly={isHOS}
                                        value={formData.resources_needed}
                                        onChange={(val) => setFormData({ ...formData, resources_needed: val })}
                                        placeholder="Textbooks, charts, tablets..."
                                    />
                                </div>
                            </div>
                        </section>

                        {/* 8. Lesson Development */}
                        <section id="structure" className="bg-white rounded-3xl shadow-sm border border-gray-200 p-8">
                            <h2 className="text-xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                                <div className="p-2 bg-indigo-100 rounded-lg text-indigo-600">8</div>
                                Lesson Development
                            </h2>

                            <div className="space-y-8">
                                <div>
                                    <label className="block text-sm font-bold text-gray-800 mb-3 ml-1">a) Introduction (Hook/Prior Knowledge)</label>
                                    <RichTextEditor
                                        compact={true}
                                        readOnly={isHOS}
                                        value={formData.introduction}
                                        onChange={(val) => setFormData({ ...formData, introduction: val })}
                                        placeholder="How will you hook the learners?"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-800 mb-3 ml-1">b) Lesson Activities (Learner-Centered)</label>
                                    <RichTextEditor
                                        compact={true}
                                        readOnly={isHOS}
                                        value={formData.development}
                                        onChange={(val) => setFormData({ ...formData, development: val })}
                                        placeholder="Transition into group work, projects, or inquiry..."
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-800 mb-3 ml-1">c) Conclusion (Reflect/Summarize)</label>
                                    <RichTextEditor
                                        compact={true}
                                        readOnly={isHOS}
                                        value={formData.conclusion}
                                        onChange={(val) => setFormData({ ...formData, conclusion: val })}
                                        placeholder="How will learners summarize their findings?"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* 9. Assessment Methods */}
                        <section id="assessment" className="bg-white rounded-3xl shadow-sm border border-gray-200 p-8">
                            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                                <div className="p-2 bg-pink-100 rounded-lg text-pink-600">9</div>
                                Assessment Methods
                            </h2>
                            <RichTextEditor
                                compact={true}
                                readOnly={isHOS}
                                value={formData.assessment_strategies}
                                onChange={(val) => setFormData({ ...formData, assessment_strategies: val })}
                                placeholder="Observation, oral questioning, rubrics..."
                            />
                        </section>

                        {/* 10. Differentiation */}
                        <section id="inclusivity" className="bg-white rounded-3xl shadow-sm border border-gray-200 p-8">
                            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                                <div className="p-2 bg-cyan-100 rounded-lg text-cyan-600">10</div>
                                Differentiation / Inclusivity
                            </h2>
                            <RichTextEditor
                                compact={true}
                                readOnly={isHOS}
                                value={formData.differentiation}
                                onChange={(val) => setFormData({ ...formData, differentiation: val })}
                                placeholder="How will you support diverse learning needs?"
                            />
                        </section>

                        {/* 11. Reflection */}
                        <section id="reflection" className="bg-white rounded-3xl shadow-sm border border-gray-200 p-8">
                            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                                <div className="p-2 bg-rose-100 rounded-lg text-rose-600">11</div>
                                Teacher's Reflection
                            </h2>
                            <div className="bg-rose-50 rounded-2xl p-4 mb-6 border border-rose-100">
                                <p className="text-xs text-rose-800 font-medium">
                                    Fill this in after the lesson to track effectiveness and student mastery.
                                </p>
                            </div>
                            <RichTextEditor
                                compact={true}
                                readOnly={isHOS}
                                value={formData.reflection}
                                onChange={(val) => setFormData({ ...formData, reflection: val })}
                                placeholder="What worked? What didn't? Mastered? Not mastered?"
                            />
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LessonPlanEditor;
